import React, { useEffect, useState } from 'react';
import { Alert, Card, Space, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { vendorApi } from '@/utils/vendorApi';
import { useAuthStore } from '@/store/authStore';
import { canEnterTransfers } from '@/store/transferAccess';
import { fileNameFromTransferId, transferStatusDisplay } from '@/components/transfer/transferStatus';
import { matchesSelectedBu } from '@/utils/businessUnits';
import { TransferItem } from '@/types';

const { Title, Text } = Typography;

function formatTime(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm') : '—';
}

const RecentTransfersCard: React.FC = () => {
  const { authorization, currentBusinessUnit } = useAuthStore();
  const [rows, setRows] = useState<TransferItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const current = useAuthStore.getState().authorization;
    if (!canEnterTransfers(current)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await vendorApi.listTransfers({ limit: 25 });
        if (!cancelled) {
          setRows(response.transfers);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedId = currentBusinessUnit?.id;
  const visible = (selectedId
    ? rows.filter((row) => matchesSelectedBu(row.bu, selectedId, authorization?.catalog))
    : rows
  ).slice(0, 5);

  return (
    <Card>
      <Title level={4} style={{ marginTop: 0 }}>Recent transfers</Title>
      {loading ? (
        <Spin />
      ) : failed ? (
        <Alert type="error" showIcon message="Couldn't load transfers." />
      ) : visible.length === 0 ? (
        <Text type="secondary">No recent transfers.</Text>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {visible.map((row) => {
            const fileName = fileNameFromTransferId(row.transferId);
            const status = transferStatusDisplay(row.status);
            return (
              <div key={row.transferId}>
                <Text>{row.vendorId || '—'}</Text>
                <Text style={{ marginLeft: 8 }}>{fileName || '—'}</Text>
                <Tag color={status.color} style={{ marginLeft: 8 }}>{status.label}</Tag>
                <Text type="secondary" style={{ marginLeft: 8 }}>{formatTime(row.lastTransitionAt)}</Text>
              </div>
            );
          })}
        </Space>
      )}
      {!loading && (
        <div style={{ marginTop: 16 }}>
          <Link to="/transfers">View all transfers</Link>
        </div>
      )}
    </Card>
  );
};

export default RecentTransfersCard;
